import type { NotificationWidgetVariant } from '@root/widgets/notification-widgets/model'

/** Only the first row may hold two widgets, every other row holds one. */
export const PAIRED_ROW_SIZE = 2

export type WidgetRows<T extends string> = Array<Array<T>>

type WidgetPairSide = 'start' | 'end'

export type WidgetDropTarget =
  | { kind: 'pair'; side: WidgetPairSide }
  | { kind: 'row'; index: number }

export const toWidgetRows = <T extends string>(
  order: Array<T>,
  variants: Partial<Record<T, NotificationWidgetVariant>> = {},
): WidgetRows<T> => {
  const [first, second, ...rest] = order
  const paired =
    first !== undefined &&
    second !== undefined &&
    variants[first] === 'square' &&
    variants[second] === 'square'

  return paired
    ? [[first, second], ...rest.map((widget) => [widget])]
    : order.map((widget) => [widget])
}

export const widgetRowsVariants = <T extends string>(
  rows: WidgetRows<T>,
): Record<T, NotificationWidgetVariant> =>
  rows.reduce(
    (variants, row) => {
      for (const widget of row) {
        variants[widget] = row.length === PAIRED_ROW_SIZE ? 'square' : 'row'
      }
      return variants
    },
    {} as Record<T, NotificationWidgetVariant>,
  )

/** Keeps the matching widgets and drops the rows that end up empty. */
export const filterWidgetRows = <T extends string>(
  rows: WidgetRows<T>,
  predicate: (widget: T) => boolean,
): WidgetRows<T> =>
  rows.map((row) => row.filter(predicate)).filter((row) => row.length > 0)

export const widgetPairSide = <T extends string>(
  rows: WidgetRows<T>,
  widget: T,
): WidgetPairSide | undefined => {
  const [pair] = rows
  if (pair?.length !== PAIRED_ROW_SIZE) {
    return undefined
  }
  if (pair[0] === widget) {
    return 'start'
  }
  if (pair[1] === widget) {
    return 'end'
  }
  return undefined
}

export const applyWidgetDropTarget = <T extends string>(
  rows: WidgetRows<T>,
  widget: T,
  target: WidgetDropTarget,
): WidgetRows<T> => {
  const others = filterWidgetRows(rows, (item) => item !== widget)

  if (target.kind === 'pair') {
    const [first = [], ...rest] = others
    const [neighbour, ...displaced] = first
    const pair =
      target.side === 'start' ? [widget, neighbour] : [neighbour, widget]

    return [
      pair.filter((item) => item !== undefined),
      ...displaced.map((item) => [item]),
      ...rest,
    ]
  }

  // A full-width row cannot sit above the paired row, so it lands right below it.
  const index =
    target.index === 0 && others[0]?.length === PAIRED_ROW_SIZE
      ? 1
      : target.index

  const next = [...others]
  next.splice(Math.max(0, Math.min(index, next.length)), 0, [widget])
  return next
}
